import {useQueries} from '@github-ui/react-query'
import {useCallback, useMemo, useState} from 'react'

import {type TimelineIssue, type TimelineItemsPageParams, timelineQueryOptions} from './fetch-timeline-items'

/**
 * # Reading the timeline in sections
 *
 * A long timeline is read as a few **sections** with gaps between them: the oldest comments, the
 * newest comments, and -- when the reader follows a permalink -- a group around the comment they
 * were sent to. Each section is one read of the timeline endpoint: params in, one result out.
 *
 * Every section is an ordinary observed query, including the pages "load more" opens. That is what
 * this file is for. It means invalidation, refetching and optimistic writes reach each of them the
 * same way, and every one of them keeps up with the server on its own.
 *
 * All the sections are read in a single `useQueries`, so the caller passes an array of sections and
 * gets an array of results lined up with it. `select` picks out the sections of one role in the
 * order they render, and `baseIndex` finds the section a role starts from.
 *
 * A section's identity is its params, because that is what TanStack keys the cache by. That is what
 * `alreadyFetched` follows from: this page's rows are already in the cache, so mounting it reuses
 * them rather than refetching. Invalidation still reaches it.
 */

/** Which part of the timeline a section covers. A base section and its "load more" pages share one. */
export type SectionRole = 'front' | 'back' | 'highlight-before' | 'highlight' | 'highlight-after'

export type TimelineSection = {
  /** `front` is the oldest end of the timeline, `back` the newest. */
  role: SectionRole
  params: TimelineItemsPageParams
  /** The section this role starts from, as opposed to a page opened beside it with "load more". */
  isBase?: boolean
  /** Rows already in the cache, so mounting must not refetch them. */
  alreadyFetched?: boolean
  enabled?: boolean
}

export type TimelineSections = {
  /**
   * What each section's read returned, in the order the sections were given.
   *
   * Each one is the issue with that section's slice of `timelineItems` on it. It is the issue and
   * not the bare connection because an optimistic write finds its connection by matching the owner
   * `id` inside the value it is applied to.
   */
  results: Array<TimelineIssue | undefined>
  loading: boolean[]
  /** Picks the entries for one role out of any array lined up with the sections, in render order. */
  select: <T>(values: T[], role: SectionRole) => Array<T | undefined>
  /** -1 when the role has no base section, which reads as `undefined` from a lined-up array. */
  baseIndex: (role: SectionRole) => number
}

export function useTimelineSections({
  owner,
  repo,
  number,
  sections,
}: {
  owner: string
  repo: string
  number: number
  sections: TimelineSection[]
}): TimelineSections {
  const queries = useMemo(
    () =>
      sections.map(section => ({
        ...timelineQueryOptions(owner, repo, number, section.params),
        ...(section.alreadyFetched ? {staleTime: Infinity} : {}),
        ...(section.enabled === false ? {enabled: false} : {}),
      })),
    [owner, repo, number, sections],
  )

  const queryResults = useQueries({
    queries,
    combine: entries => ({
      results: entries.map(entry => entry.data),
      loading: entries.map(entry => entry.isLoading),
    }),
  })

  const select = useCallback(
    <T>(values: T[], role: SectionRole) =>
      sections.flatMap((section, index) => (section.role === role ? [values[index]] : [])),
    [sections],
  )

  const baseIndex = useCallback(
    (role: SectionRole) => sections.findIndex(section => section.role === role && section.isBase),
    [sections],
  )

  return {
    results: queryResults.results,
    loading: queryResults.loading,
    select,
    baseIndex,
  }
}

/**
 * The pages "load more" has read, kept as the params that read them rather than the rows they
 * returned: rows are a snapshot, and a snapshot cannot follow an invalidation, so such a page would
 * keep showing a comment after someone deleted it.
 *
 * Each list is kept in the order the pages render, oldest first, which is the reverse of the order
 * they were read in on the newest side and before a permalink.
 */
export function useLoadedPages(
  /** Pages are kept per permalink, so soft-navigating between two does not mix them. */
  focusText: string | undefined,
) {
  const [front, setFront] = useState<TimelineItemsPageParams[]>([])
  const [back, setBack] = useState<TimelineItemsPageParams[]>([])
  const [highlightByFocus, setHighlightByFocus] = useState<Record<string, TimelineSection[]>>({})

  const highlight = useMemo(() => (focusText ? (highlightByFocus[focusText] ?? []) : []), [highlightByFocus, focusText])

  /** Reading forward from the oldest end, so each page renders after the ones before it. */
  const addFront = useCallback((params: TimelineItemsPageParams) => setFront(pages => [...pages, params]), [])

  /** Reading backward from the newest end, so each page renders before the ones already read. */
  const addBack = useCallback((params: TimelineItemsPageParams) => setBack(pages => [params, ...pages]), [])

  const addHighlight = useCallback(
    (direction: 'before' | 'after') => (params: TimelineItemsPageParams) => {
      if (!focusText) return
      const role: SectionRole = direction === 'before' ? 'highlight-before' : 'highlight-after'
      const page: TimelineSection = {role, params, alreadyFetched: true}
      setHighlightByFocus(byFocus => {
        const kept = byFocus[focusText] ?? []
        return {...byFocus, [focusText]: direction === 'before' ? [page, ...kept] : [...kept, page]}
      })
    },
    [focusText],
  )

  return {front, back, highlight, addFront, addBack, addHighlight}
}
